import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';

import { MatRadioButton } from '@angular/material/radio';
import { countryCodeListModel } from '../../../models/countryCodeListModel';
import { CountryCode } from '../../../models/countryCodeModel';
import { phoneNumberValidator } from '../../validators/phoneNumberValidator';

@Component({
  selector: 'app-radio-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './radio-select.component.html',
  standalone: false,
})
export class RadioSelectComponent implements OnInit {
  @Input() formRef: UntypedFormGroup;

  @Input() reporter: any;

  @Input() inlineErrors: Record<string, string | null> = {};

  @Input() altContact: boolean = true;

  @Output() changeContact = new EventEmitter();

  @ViewChild('alternatePhoneRadioButton')
  alternatePhoneRadioButton: MatRadioButton;

  altPhoneCountry: string;

  countryCodes: CountryCode[] = countryCodeListModel();

  isCountryDropdownOpen = false;

  countrySearchTerm = '';

  AltPhone = 'Alternate Phone';

  @HostListener('document:click')
  onDocumentClick(): void {
    this.isCountryDropdownOpen = false;
    this.countrySearchTerm = '';
  }

  get filteredCountryCodes(): CountryCode[] {
    if (!this.countrySearchTerm) return this.countryCodes;

    const term = this.countrySearchTerm.toLowerCase();
    return this.countryCodes.filter(
      (country) =>
        country.name.toLowerCase().includes(term) ||
        country.dialCode.includes(term),
    );
  }

  get selectedCountryLabel(): string {
    const code = this.formRef?.get('countryCode')?.value;
    if (!code) return '';

    const selectedCountry = this.countryCodes.find(
      (country) => country.iso2.toLowerCase() === `${code}`.toLowerCase(),
    );

    return selectedCountry?.name ?? '';
  }

  get alternatePhoneErrorMessage(): string {
    const hasCountryError = this.inlineErrors?.countryCode;
    const hasPhoneError =
      this.formRef?.errors?.isValidPhoneNumber ||
      this.formRef?.controls?.alternatePhone?.errors?.required;

    if (hasCountryError && hasPhoneError) {
      return 'Please select a country and enter a valid phone number';
    }
    if (hasCountryError) {
      return 'Please select a country';
    }
    if (hasPhoneError) {
      return 'Please enter a valid phone number';
    }

    return '';
  }

  ngOnInit(): void {
    this.formRef.addValidators(phoneNumberValidator());

    if (!this.formRef.get('preferredContactMethod')?.value) {
      const defaultMethod = this.reporter?.Email
        ? 'email'
        : this.reporter?.Phone
          ? 'phone'
          : null;
      if (defaultMethod) {
        this.formRef.get('preferredContactMethod')?.setValue(defaultMethod);
        this.setAltContactValues(null, false, false);
      }
    }
  }

  onRadioChange(): void {
    // this.showAltContactOptions = false;
    if (
      this.formRef.get('preferredContactMethod').value === 'Alternate Phone'
    ) {
      this.setAltContactValues(this.AltPhone, true, false);
    } else {
      this.setAltContactValues(null, false, true);
    }
    this.changeContact.emit();
  }

  onAltContactValueChange() {
    this.formRef.get('alternatePhone').statusChanges.subscribe((val) => {
      if (val === 'INVALID') {
        this.inlineErrors.alternatePhone = 'Invalid phone number.';
      } else {
        this.inlineErrors.alternatePhone = '';
      }
    });
  }

  selectAlternatePhoneRadioButton() {
    this.alternatePhoneRadioButton.checked = true;
    if (this.formRef.get('preferredContactMethod').value !== this.AltPhone) {
      this.formRef.get('preferredContactMethod').setValue(this.AltPhone);
      this.setAltContactValues(this.AltPhone, true, false);
      this.changeContact.emit();
    }
  }

  toggleCountryDropdown(event: Event): void {
    event.stopPropagation();
    this.selectAlternatePhoneRadioButton();
    this.isCountryDropdownOpen = !this.isCountryDropdownOpen;
    if (!this.isCountryDropdownOpen) this.countrySearchTerm = '';
  }

  selectCountry(country: CountryCode, event: Event): void {
    event.stopPropagation();
    this.selectAlternatePhoneRadioButton();
    this.formRef.controls.countryCode?.setValue(country.iso2.toUpperCase());
    this.formRef.controls.countryCode?.markAsTouched();
    this.formRef.controls.countryCode?.updateValueAndValidity();
    this.isCountryDropdownOpen = false;
    this.countrySearchTerm = '';
    this.changeContact.emit();
  }

  onCountrySearchInput(event: Event): void {
    this.countrySearchTerm = (event.target as HTMLInputElement).value;
  }

  formatAlternatePhone(): void {
    const control = this.formRef.get('alternatePhone');
    const value = `${control?.value ?? ''}`.trim();

    if (!value || value.startsWith('+')) {
      return;
    }

    const digits = value.replace(/\D/g, '');

    if (digits.length !== 10) {
      return;
    }

    control.setValue(
      `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`,
      { emitEvent: false },
    );
  }

  altPhoneKeypress = (event: KeyboardEvent) => {
    // google-libphonenumber can do some funny things at times,
    // such as trying to map alphas to numbers.
    // Limit what the user can enter
    const allowableChars = [
      { key: 'Tab', keyCode: 0x09 },
      { key: ' ', keyCode: 0x20 },
      { key: 'Spacebar', keyCode: 0x20 },
      { key: '0', keyCode: 0x30 },
      { key: '1', keyCode: 0x31 },
      { key: '2', keyCode: 0x32 },
      { key: '3', keyCode: 0x33 },
      { key: '4', keyCode: 0x34 },
      { key: '5', keyCode: 0x35 },
      { key: '6', keyCode: 0x36 },
      { key: '7', keyCode: 0x37 },
      { key: '8', keyCode: 0x38 },
      { key: '9', keyCode: 0x39 },
      { key: 'Add', keyCode: 0xab },
      { key: '+', keyCode: 0xab },
      { key: 'Multiply', keyCode: 0xaa },
      { key: '*', keyCode: 0xaa },
      { key: '#', keyCode: '#' },
      { key: 'Decimal', keyCode: 0xbe },
      { key: '.', keyCode: 0xbe },
      { key: 'Subtract', keyCode: 0xad },
      { key: '-', keyCode: 0xad },
      { key: ',', keyCode: 0xbc },
      { key: '(', keyCode: 0xa8 },
      { key: ')', keyCode: 0xa9 },
    ];

    // event.keyCode is deprecated, use event.key if it's available.
    if (event.key) {
      if (!allowableChars.some((c) => c.key === event.key)) {
        return false;
      }
    } else if (!allowableChars.some((c) => c.keyCode === event.keyCode)) {
      return false;
    }
  };

  setAltContactValues(
    altContactMethod: string,
    showFullInputField: boolean,
    resetAltContactValue: boolean = false,
  ) {
    if (altContactMethod === this.AltPhone) {
      this.formRef.controls.alternatePhone?.setValidators([
        Validators.required,
      ]);
      this.formRef.controls.countryCode?.setValidators([Validators.required]);
      this.formRef.controls.preferredContactMethod.clearValidators();
    } else {
      this.formRef.controls.alternatePhone?.clearValidators();
      this.formRef.controls.countryCode?.clearValidators();
      this.formRef.controls.alternateContactMethod?.clearValidators();
      this.formRef.controls.preferredContactMethod.setValidators(
        Validators.required,
      );
      if (resetAltContactValue) {
        this.formRef.controls.alternatePhone?.setValue(null);
        this.formRef.controls.countryCode?.setValue(null);
        this.formRef.controls.alternateContactMethod?.setValue(null);
      }
    }
    this.formRef.controls.alternatePhone?.updateValueAndValidity();
    this.formRef.controls.countryCode?.updateValueAndValidity();
    this.formRef.controls.preferredContactMethod.updateValueAndValidity();
    this.formRef.controls.alternateContactMethod?.updateValueAndValidity();
  }
}
